import React, { useCallback, useState } from 'react';
import MoreIcon from 'spw-static/images/more.svg';
import Img from 'react-cool-img';
import { IconButton, IconButtonProps, Menu, MenuItem } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { colors } from 'spw-theme';
import { SPWCircularProgress } from '.';

const useStyles = makeStyles({
  button: {
    padding: 0,
    width: '40px',
    '&:hover': {
      backgroundColor: 'unset',
    },
  },
  menu: {
    '& ul': {
      padding: 0,
    },
    '& li': {
      fontSize: '14px',
      lineHeight: '20px',
      padding: '8px 16px',
      width: '181px',
    },
  },
});

type Props = {
  className?: string;
  loading?: boolean;
  options: { text: string; onClick: () => void; disabled?: boolean }[];
};

const MoreOptionsMenu = ({ className, loading = false, options }: Props) => {
  const classes = useStyles();
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const handleIconClick = useCallback<IconButtonProps['onClick']>(
    (e) => {
      setAnchorEl(e.currentTarget);
    },
    [setAnchorEl]
  );

  const handleCloseMenu = useCallback(() => {
    setAnchorEl(null);
  }, [setAnchorEl]);

  const handleOptionClick = useCallback(
    (onClickCallback: () => void) => {
      setAnchorEl(null);
      onClickCallback();
    },
    [setAnchorEl]
  );

  return (
    <>
      <IconButton
        aria-label="more options"
        className={classes.button}
        onClick={handleIconClick}
        size="large"
      >
        {loading ? (
          <SPWCircularProgress color={colors.mainBlue} thickness={5} />
        ) : (
          <Img src={MoreIcon} />
        )}
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        anchorOrigin={{
          horizontal: 'right',
          vertical: 'bottom',
        }}
        className={`${className ?? ''} ${classes.menu}`}
        disableScrollLock
        keepMounted
        onClose={handleCloseMenu}
        open={!!anchorEl}
        transformOrigin={{
          horizontal: 'right',
          vertical: 'top',
        }}
      >
        {options.map(({ text, onClick, disabled }) => (
          <MenuItem
            key={text}
            onClick={() => handleOptionClick(onClick)}
            disabled={disabled}
          >
            {text}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export default MoreOptionsMenu;
